import { FormatIsoDate } from '../../utils/date';
import { CountLabel, FormatInt } from '../../utils/format';
import { FormatMlMoney, FormatMlPercent } from '../../utils/mlPayloadValues';
import { SummarizePriceSuggestion, SummarizePriceSuggestionItems } from '../../utils/mlPriceSuggestions';
import { ItemId, Mono } from '../ui/Mono';

/**
 * Resumos das Referências de preços (/suggestions) no Explorador de Endpoints.
 * O JSON cru continua embaixo; aqui fica o que se lê de relance.
 */

const CARD = 'rounded-xl border border-mono-200 bg-white p-5 dark:border-mono-800 dark:bg-mono-950';

const TONES = {
    bad: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    warn: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
    good: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    promo: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    neutral: 'bg-mono-100 text-mono-800 dark:bg-mono-800 dark:text-mono-200',
};

const Stat = ({ label, children }) => (
    <div className="rounded-lg border border-mono-200 bg-mono-50 px-3 py-2 dark:border-mono-800 dark:bg-mono-900">
        <dt className="text-xs text-mono-500 dark:text-mono-400">{label}</dt>
        <dd className="mt-0.5 font-mono text-sm font-semibold tabular-nums text-black dark:text-white">{children}</dd>
    </div>
);

/** Detalhe de UM anúncio: status, preços, diferença, aplicável, custos, promoção e concorrentes. */
export const PriceSuggestionSummary = ({ payload }) => {
    const summary = SummarizePriceSuggestion(payload);
    if (!summary) {
        return null;
    }
    const { status, currency, promotion, competitors, costs } = summary;

    return (
        <section className={CARD} aria-label="Resumo da referência de preço">
            <div className="mb-3 flex flex-wrap items-center gap-2">
                <h3 className="text-sm font-semibold text-black dark:text-white">Referência de preço</h3>
                <ItemId value={summary.itemId} className="text-sm" />
            </div>

            <div className="mb-4 flex flex-wrap items-center gap-2">
                <span className={`rounded px-2 py-0.5 text-xs font-semibold ${TONES[status.tone] || TONES.neutral}`}>
                    {status.label}
                </span>
                {status.status ? (
                    <Mono className="text-xs text-mono-500 dark:text-mono-400">{status.status}</Mono>
                ) : null}
                <p className="w-full text-sm text-mono-700 dark:text-mono-300">{status.meaning}</p>
            </div>

            <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {summary.prices.map((price) => (
                    <Stat key={price.key} label={price.label}>
                        {FormatMlMoney(price.amount, currency)}
                    </Stat>
                ))}
            </dl>

            <dl className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Stat label="Diferença (atual × sugerido)">{FormatMlPercent(summary.percentDifference)}</Stat>
                <Stat label="Sugestão aplicável">
                    {summary.applicable === null ? '—' : summary.applicable ? 'Sim' : 'Não'}
                </Stat>
                <Stat label="Tarifa de venda">{FormatMlMoney(costs?.sellingFees, currency)}</Stat>
                <Stat label="Frete">{FormatMlMoney(costs?.shippingFees, currency)}</Stat>
            </dl>

            {promotion ? (
                <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm dark:border-blue-900 dark:bg-blue-950">
                    <p className="font-semibold text-blue-900 dark:text-blue-100">
                        Promoção {promotion.name || ''}
                        {promotion.promotionId ? (
                            <Mono className="ml-2 text-xs font-normal">{promotion.promotionId}</Mono>
                        ) : null}
                    </p>
                    <ul className="mt-1 space-y-0.5 text-blue-900 dark:text-blue-100">
                        {promotion.discountPercent !== null ? (
                            <li>Desconto de {FormatMlPercent(promotion.discountPercent)}</li>
                        ) : null}
                        {promotion.startDate || promotion.endDate ? (
                            <li>
                                De {FormatIsoDate(promotion.startDate)} a {FormatIsoDate(promotion.endDate)}
                            </li>
                        ) : null}
                        {promotion.unhealthyReason ? (
                            <li>
                                Motivo: {promotion.unhealthyReason}
                                {promotion.daysUnhealthy !== null
                                    ? ` há ${CountLabel(promotion.daysUnhealthy, 'dia')}`
                                    : ''}
                            </li>
                        ) : null}
                    </ul>
                </div>
            ) : null}

            {competitors.total > 0 ? (
                <div className="mt-4">
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-mono-500 dark:text-mono-400">
                        Concorrentes comparados: {FormatInt(competitors.compared ?? competitors.total)}
                    </p>
                    <ul className="space-y-1 text-sm">
                        {competitors.items.map((item, index) => (
                            <li key={index} className="flex flex-wrap items-baseline gap-x-3">
                                <span className="text-black dark:text-white">{item.title || '(sem título)'}</span>
                                <Mono className="text-mono-700 dark:text-mono-300">
                                    {FormatMlMoney(item.amount, currency)}
                                </Mono>
                                {item.soldQuantity !== null ? (
                                    <span className="text-xs text-mono-500 dark:text-mono-400">
                                        {CountLabel(item.soldQuantity, 'vendido')}
                                    </span>
                                ) : null}
                            </li>
                        ))}
                    </ul>
                    {competitors.total > competitors.items.length ? (
                        <p className="mt-1 text-xs text-mono-500 dark:text-mono-400">
                            e mais {competitors.total - competitors.items.length} na resposta crua
                        </p>
                    ) : null}
                </div>
            ) : null}

            {summary.lastUpdated ? (
                <p className="mt-4 text-xs text-mono-500 dark:text-mono-400">
                    Referência atualizada em <Mono>{summary.lastUpdated}</Mono>
                </p>
            ) : null}
        </section>
    );
};

/** Lista de anúncios com referência: cada MLB abre o detalhe da referência dele. */
export const PriceSuggestionItems = ({ payload, onOpenItem }) => {
    const summary = SummarizePriceSuggestionItems(payload);
    if (!summary) {
        return null;
    }

    return (
        <section className={CARD} aria-label="Anúncios com referência de preço">
            <h3 className="mb-3 text-sm font-semibold text-black dark:text-white">
                {CountLabel(summary.total, 'anúncio com referência', 'anúncios com referência')}
            </h3>
            {summary.items.length === 0 ? (
                <p className="text-sm text-mono-500 dark:text-mono-400">Nenhum anúncio com referência de preço.</p>
            ) : (
                <div className="flex max-h-64 flex-wrap gap-2 overflow-auto">
                    {summary.items.map((itemId) => (
                        <button
                            key={itemId}
                            type="button"
                            onClick={() => onOpenItem?.(itemId)}
                            title={`Abrir a referência de preço de ${itemId}`}
                            className="rounded-lg border border-mono-300 px-2 py-1 font-mono text-xs tabular-nums text-mono-800 hover:bg-mono-100 dark:border-mono-700 dark:text-mono-200 dark:hover:bg-mono-900"
                        >
                            {itemId}
                        </button>
                    ))}
                </div>
            )}
        </section>
    );
};
